/**
 * WebSocket integration for React Query
 * Listens to WebSocket events and invalidates React Query cache automatically
 * This ensures the UI stays in sync with real-time backend updates
 */

import { useEffect, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useWebSocket } from '@/utils/contexts/WebSocketContext';
import { videoKeys } from '../../../api/querry_keys';
import { fetchVideoById } from '@/api/videoApi';

/**
 * Hook that connects WebSocket events to React Query cache invalidation
 * Automatically invalidates and refetches queries when relevant events occur
 *
 * WebSocket Events Handled:
 * - video_generated: New video created (invalidates video lists)
 * - video_uploaded: Video uploaded (invalidates video lists)
 * - video_updated: Video status/metadata changed (invalidates video lists)
 * - thumbnail_updated: Video thumbnail generated (invalidates video lists & detail)
 * - video_deleted: Video removed (invalidates video lists)
 * - video_pinned: Video pinned (invalidates video lists & pinned videos)
 * - video_unpinned: Video unpinned (invalidates video lists & pinned videos)
 * - image_generated: New image created (invalidates image lists)
 * - image_updated: Image status/metadata changed (invalidates image lists)
 * - admin_video_popup: Admin notification (triggers video list refetch)
 */
export const useWebSocketSync = (userEmail?: string | null, isAdmin: boolean = false) => {
  const queryClient = useQueryClient();
  const { subscribe, unsubscribe } = useWebSocket();

  // Helper to invalidate video lists
  const invalidateVideoLists = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: videoKeys.lists() });
    if (isAdmin) {
      await queryClient.invalidateQueries({ queryKey: videoKeys.admin() });
    }
  }, [queryClient, isAdmin]);

  // Helper to invalidate pinned videos
  const invalidatePinnedVideos = useCallback(async () => {
    // Mark as stale but don't auto-refetch to avoid duplicate requests
    // The specific component's WebSocket handler will control when to refetch
    await queryClient.invalidateQueries({ 
      queryKey: videoKeys.pinnedVideos(true),
      refetchType: 'none' // Don't auto-refetch
    }); // Admin cache
    await queryClient.invalidateQueries({ 
      queryKey: videoKeys.pinnedVideos(false),
      refetchType: 'none' // Don't auto-refetch
    }); // User cache
  }, [queryClient]);

  // Helper to invalidate video status counts
  const invalidateVideoStatusCounts = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ['videoStatusCounts'] });
  }, [queryClient]);

  // Helper to invalidate image lists
  const invalidateImageLists = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ['images'] });
    if (isAdmin) {
      await queryClient.invalidateQueries({ queryKey: ['images', 'adminAll'] });
    }
  }, [queryClient, isAdmin]);

  // Helper to invalidate image status counts
  const invalidateImageStatusCounts = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ['imageStatusCounts'] });
  }, [queryClient]);

  // Event handlers
  const handleVideoCreation = useCallback(async () => {
    await invalidateVideoLists();
  }, [invalidateVideoLists]);

  const handleVideoUpdate = useCallback(async (eventData: any) => {
    // Optimistically update cached data BEFORE refetching
    if (eventData?.video_id) {
      const videoId = eventData.video_id;
      const newData = eventData;

      // Helper function to update video in array
      const updateVideoInArray = (videos: any[]) => 
        videos.map((video: any) => 
          video.id === videoId ? { ...video, ...newData } : video
        );

      // Update all video list caches optimistically
      queryClient.setQueriesData(
        { queryKey: videoKeys.lists() },
        (oldData: any) => {
          if (!oldData) return oldData;
          
          // Handle array of videos
          if (Array.isArray(oldData)) {
            return updateVideoInArray(oldData);
          }
          
          // Handle paginated data structure
          if (oldData.pages) {
            return {
              ...oldData,
              pages: oldData.pages.map((page: any) => ({
                ...page,
                videos: page.videos ? updateVideoInArray(page.videos) : page.videos
              }))
            };
          }
          
          return oldData;
        }
      );

      // Update pinned videos cache optimistically
      queryClient.setQueriesData(
        { queryKey: videoKeys.pinnedVideos(true) }, // Admin cache
        (oldData: any) => {
          if (!oldData || !Array.isArray(oldData)) return oldData;
          return updateVideoInArray(oldData);
        }
      );

      queryClient.setQueriesData(
        { queryKey: videoKeys.pinnedVideos(false) }, // User cache
        (oldData: any) => {
          if (!oldData || !Array.isArray(oldData)) return oldData;
          return updateVideoInArray(oldData);
        }
      );

      // Update video detail cache
      queryClient.setQueryData(
        videoKeys.detail(videoId),
        (oldData: any) => oldData ? { ...oldData, ...newData } : oldData
      );
    }

    // Now invalidate to refetch fresh data in background
    await invalidateVideoLists();
    await invalidateVideoStatusCounts();
    await invalidatePinnedVideos();
    if (eventData?.video_id) {
      await queryClient.invalidateQueries({ queryKey: videoKeys.detail(eventData.video_id) });
    }
  }, [invalidateVideoLists, invalidateVideoStatusCounts, invalidatePinnedVideos, queryClient]);

  const handleThumbnailUpdate = useCallback(async (eventData: any) => {
    // When thumbnail is generated, invalidate video lists and specific video detail
    if (eventData?.video_id) {
      await queryClient.invalidateQueries({ queryKey: videoKeys.detail(eventData.video_id) });
    }
    await invalidateVideoLists();
  }, [invalidateVideoLists, queryClient]);

  const handleVideoDelete = useCallback(async (eventData: any) => {
    await invalidateVideoLists();
    await invalidateVideoStatusCounts();
    if (eventData?.video_id) {
      queryClient.removeQueries({ queryKey: videoKeys.detail(eventData.video_id) });
    }
  }, [invalidateVideoLists, invalidateVideoStatusCounts, queryClient]);

  // Helper to find video in query data (generic for any query key)
  const findVideoInQueryData = useCallback((queryKey: any, videoId: string): any => {
    let videoToAdd: any = null;
    queryClient.getQueriesData({ queryKey }).some(([_key, data]: [any, any]) => {
      if (Array.isArray(data)) {
        videoToAdd = data.find((v: any) => v.id === videoId);
        return !!videoToAdd;
      }
      if (data?.pages) {
        for (const page of data.pages) {
          if (page.videos) {
            videoToAdd = page.videos.find((v: any) => v.id === videoId);
            if (videoToAdd) return true;
          }
        }
      }
      return false;
    });
    return videoToAdd;
  }, [queryClient]);

  // Helper to find or fetch video for pinning
  const findOrFetchVideo = useCallback(async (videoId: string): Promise<any> => {
    // Search in user lists
    let videoToAdd = findVideoInQueryData(videoKeys.lists(), videoId);
    
    // Search in admin lists if not found
    if (!videoToAdd) {
      videoToAdd = findVideoInQueryData(videoKeys.admin(), videoId);
    }

    // Fetch from API if still not found
    if (!videoToAdd) {
      try {
        videoToAdd = await fetchVideoById(videoId, isAdmin);
      } catch {
        // Video fetch failed, videoToAdd remains null
        videoToAdd = null;
      }
    }

    return videoToAdd;
  }, [findVideoInQueryData, isAdmin]);

  const handleVideoPinChange = useCallback(async (eventData: any) => {
    // Optimistically update pin status immediately in ALL caches simultaneously
    if (!eventData?.video_id) {
      return;
    }

    const videoId = eventData.video_id;
    const isPinned = eventData?.event === 'video_pinned';
    
    // Find the video to add to pinned array (only needed when pinning)
    let videoToAdd: any = null;
    if (isPinned) {
      videoToAdd = await findOrFetchVideo(videoId);
    }

    // Helper function to update video pin status in main lists
    const updateVideoPinStatus = (videos: any[]) =>
      videos.map((video: any) =>
        video.id === videoId ? { ...video, pinned: isPinned, is_pinned: isPinned } : video
      );

    // Helper function to update pinned videos array (add/remove based on pin status)
    const updatePinnedVideosArray = (videos: any[]) => {
      if (isPinned) {
        // If pinning, check if video already exists in array
        const exists = videos.some((v: any) => v.id === videoId);
        if (exists) {
          // Video exists, just update its pinned status
          return videos.map((video: any) =>
            video.id === videoId ? { ...video, pinned: true, is_pinned: true } : video
          );
        }
        // Video doesn't exist, add it to the beginning of the array
        if (videoToAdd) {
          return [{ ...videoToAdd, pinned: true, is_pinned: true }, ...videos];
        }
        return videos;
      } else {
        // If unpinning, remove video from pinned array
        return videos.filter((video: any) => video.id !== videoId);
      }
    };

    // Helper to update pages in paginated data
    const updatePagesWithVideos = (pages: any[]) => 
      pages.map((page: any) => ({
        ...page,
        videos: page.videos ? updateVideoPinStatus(page.videos) : page.videos
      }));

    // Helper function to update cache for a specific query key
    const updateCacheForQueryKey = (queryKey: any) => {
      queryClient.setQueriesData(
        { queryKey },
        (oldData: any) => {
          if (!oldData) return oldData;
          if (Array.isArray(oldData)) {
            return updateVideoPinStatus(oldData);
          }
          if (oldData.pages) {
            return {
              ...oldData,
              pages: updatePagesWithVideos(oldData.pages)
            };
          }
          return oldData;
        }
      );
    };

    // Use React's batch update to apply all cache updates synchronously
    // This ensures both video lists and pinned videos update at the same time
    
    // Update user and admin video lists
    updateCacheForQueryKey(videoKeys.lists());
    updateCacheForQueryKey(videoKeys.admin());

    // Update pinned videos caches (both admin and user) - happens synchronously
    queryClient.setQueriesData(
      { queryKey: videoKeys.pinnedVideos(true) }, // Admin cache
      (oldData: any) => {
        if (!oldData || !Array.isArray(oldData)) return oldData;
        return updatePinnedVideosArray(oldData);
      }
    );

    queryClient.setQueriesData(
      { queryKey: videoKeys.pinnedVideos(false) }, // User cache
      (oldData: any) => {
        if (!oldData || !Array.isArray(oldData)) return oldData;
        return updatePinnedVideosArray(oldData);
      }
    );

    // Update video detail cache
    queryClient.setQueryData(
      videoKeys.detail(videoId),
      (oldData: any) => {
        return oldData ? { ...oldData, pinned: isPinned, is_pinned: isPinned } : oldData;
      }
    );

    // No need to invalidate - optimistic updates above already updated all caches
    // This prevents duplicate GET requests while maintaining real-time UI updates
  }, [queryClient, findOrFetchVideo]);

  const handleAdminNotification = useCallback(async () => {
    if (isAdmin) {
      await queryClient.invalidateQueries({ queryKey: videoKeys.admin() });
      await invalidateVideoStatusCounts();
    }
  }, [isAdmin, queryClient, invalidateVideoStatusCounts]);

  // Image event handlers
  const handleImageCreation = useCallback(async () => {
    await invalidateImageLists();
  }, [invalidateImageLists]);

  const handleImageUpdate = useCallback(async (eventData: any) => {
    // Check if this is the admin who made the change
    const isCurrentAdmin = isAdmin && userEmail && eventData?.admin_email === userEmail;
    
    if (isCurrentAdmin && eventData?.admin_optimistic) {
      // Skip refetch - optimistic update already happened
      return;
    }

    // For other users/admins, invalidate to get fresh data
    await invalidateImageLists();
    await invalidateImageStatusCounts();
  }, [invalidateImageLists, invalidateImageStatusCounts, isAdmin, userEmail]);

  const handleImageDelete = useCallback(async (_eventData: any) => {
    await invalidateImageLists();
    await invalidateImageStatusCounts();
  }, [invalidateImageLists, invalidateImageStatusCounts]);

  useEffect(() => {
    const handleWebSocketMessage = async (data: any) => {
      try {
        const parsedData = typeof data === 'string' ? JSON.parse(data) : data;
        if (!parsedData?.event) return;

        const { event, data: eventData } = parsedData;

        // Map events to handlers
        switch (event) {
          case 'video_generated':
          case 'video_uploaded':
            await handleVideoCreation();
            break;
          case 'video_updated':
            await handleVideoUpdate(eventData);
            break;
          case 'thumbnail_updated':
            await handleThumbnailUpdate(eventData);
            break;
          case 'video_deleted':
            await handleVideoDelete(eventData);
            break;
          case 'video_pinned':
          case 'video_unpinned':
            await handleVideoPinChange({ ...eventData, event });
            break;
          case 'image_generated':
            await handleImageCreation();
            break;
          case 'image_updated':
            await handleImageUpdate(eventData);
            break;
          case 'image_deleted':
            await handleImageDelete(eventData);
            break;
          case 'admin_video_popup':
            await handleAdminNotification();
            break;
        }
      } catch (error) {
        console.error('Error handling WebSocket message in React Query sync:', error);
      }
    };

    const componentId = `websocket-sync-${userEmail || 'global'}-${isAdmin ? 'admin' : 'user'}`;
    subscribe(componentId, handleWebSocketMessage);

    return () => {
      unsubscribe(componentId);
    };
  }, [
    subscribe,
    unsubscribe,
    userEmail,
    isAdmin,
    handleVideoCreation,
    handleVideoUpdate,
    handleThumbnailUpdate,
    handleVideoDelete,
    handleVideoPinChange,
    handleImageCreation,
    handleImageUpdate,
    handleImageDelete,
    handleAdminNotification
  ]);
};
